import { getFoldersQueryName } from '@app/components/folder-tree/helper';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

type FolderQueryContextType = {
  queryName: string;
  setQueryName: (queryName: string) => void;
};

const FolderQueryContext = createContext<FolderQueryContextType | null>(null);

export const FolderQueryProvider = ({ children }: { children: React.ReactNode }) => {
  const [queryName, setQueryName] = useState<string>(() => getFoldersQueryName(false));

  const value = useMemo(
    () => ({
      queryName,
      setQueryName,
    }),
    [
      queryName,
    ],
  );
  return <FolderQueryContext.Provider value={value}>{children}</FolderQueryContext.Provider>;
};

export const useFolderQueryContext = () => {
  return useContext(FolderQueryContext);
};

export const useSyncQueryName = (queryName: string) => {
  const context = useFolderQueryContext();

  // biome-ignore lint/correctness/useExhaustiveDependencies: no need to include setQueryName in the dependencies as it is a react setState function
  useEffect(() => {
    if (!context) {
      return;
    }

    context.setQueryName(queryName);
  }, [
    queryName,
  ]);
};

export const withFolderQueryName = (Component: React.ComponentType<any>) => (props: any) => {
  const context = useFolderQueryContext();
  const queryName = context?.queryName ?? getFoldersQueryName(false);

  return (
    <Component
      {...props}
      folderQueryName={queryName}
    />
  );
};

export const withFolderQueryContextProvider =
  (Component: React.ComponentType<any>) => (props: any) => {
    return (
      <FolderQueryProvider>
        <Component {...props} />
      </FolderQueryProvider>
    );
  };
